// Coalesces `account.cache_invalidated` events in one browser.
//
// The server broadcasts each event to every open dashboard of the account, and
// each revalidation refetches from the network. A burst of events must not turn
// into a burst of refetches: the revalidator keeps the newest key per model,
// waits until the burst is quiet plus a random delay per browser, so dashboards
// do not refetch at the same moment, and runs one revalidation per model at a
// time. A key that arrives during a revalidation runs once more at its end.

export const QUIET_PERIOD_MS = 1000;
export const MAX_JITTER_MS = 3000;

// cache key name from the server => Vuex module with a `revalidate` action
const STORE_MODULES = { label: 'labels', inbox: 'inboxes', team: 'teams' };

// Keys are Unix timestamps as strings. An event can arrive late, so an older
// key must not replace a newer one.
const newerKey = (currentKey, nextKey) => {
  if (currentKey === undefined) return nextKey;
  return Number(nextKey) >= Number(currentKey) ? nextKey : currentKey;
};

export default class CacheRevalidator {
  constructor(
    dispatch,
    { quietPeriodMs = QUIET_PERIOD_MS, maxJitterMs = MAX_JITTER_MS } = {}
  ) {
    this.dispatch = dispatch;
    this.quietPeriodMs = quietPeriodMs;
    this.maxJitterMs = maxJitterMs;
    this.pendingKeys = {};
    this.timer = null;
    this.jitterMs = null;
    // models with a revalidation in flight
    this.running = new Set();
    // model => newest key that arrived during its revalidation
    this.queuedKeys = new Map();
  }

  push(cacheKeys = {}) {
    Object.keys(STORE_MODULES).forEach(model => {
      const key = cacheKeys[model];
      if (key === undefined || key === null) return;
      this.pendingKeys[model] = newerKey(this.pendingKeys[model], key);
    });

    // The random delay is chosen once per burst; every event restarts the wait.
    if (this.jitterMs === null) {
      this.jitterMs = Math.random() * this.maxJitterMs;
    }
    clearTimeout(this.timer);
    this.timer = setTimeout(
      () => this.flush(),
      this.quietPeriodMs + this.jitterMs
    );
  }

  flush() {
    const keys = this.pendingKeys;
    this.pendingKeys = {};
    this.timer = null;
    this.jitterMs = null;
    Object.entries(keys).forEach(([model, key]) => this.revalidate(model, key));
  }

  async revalidate(model, newKey) {
    if (this.running.has(model)) {
      this.queuedKeys.set(model, newerKey(this.queuedKeys.get(model), newKey));
      return;
    }

    this.running.add(model);
    let key = newKey;
    try {
      while (key !== undefined) {
        // The revalidations of one model must run in sequence.
        // eslint-disable-next-line no-await-in-loop
        await this.dispatch(`${STORE_MODULES[model]}/revalidate`, {
          newKey: key,
        });
        key = this.queuedKeys.get(model);
        this.queuedKeys.delete(model);
      }
    } finally {
      this.running.delete(model);
    }
  }
}
